import { useMemo } from "react";
import { useCurrentUser } from "../../../utils/hooks";
import { useFieldValue, useTemplateStoreFromContext } from "../Context/hooks";
import Static from "../Static";
import { staticIsNotRenderable } from "../Static/StaticBase.component";
import {
  ContentItemDecorator,
  FieldContentItem as FieldContentItemType,
} from "../types";
import FieldTextDisplay from "./Display/FieldTextDisplay";
import InputFieldBase, { InputComponentFor } from "./Inputs";
import { FieldPeriodStamp } from "./FieldPeriodStamp";
import { useShallow } from "zustand/react/shallow";

function FieldContent({
  metadata,
  templateKey,
  field,
}: {
  metadata: any;
  templateKey: string;
  field?: any;
}): JSX.Element | null {
  // The period-scoped (Ongoing Reporting) tab sets `period`; the standard DD tabs don't.
  const { period } = useTemplateStoreFromContext()(
    useShallow((state: any) => ({ period: state.period }))
  );

  const content =
    metadata.displayType === "textarea" ? (
      <FieldTextDisplay
        templateKey={templateKey}
        metadata={metadata}
        notApplicable={field?.notApplicable}
      />
    ) : (
      <Static
        displayType={metadata.displayType as string}
        value={field?.value}
        notApplicable={field?.notApplicable}
        metadata={metadata}
      />
    );

  // In the period-scoped view, show the reporting PERIOD each field was last updated for
  // (from the carried-forward value's asOfDate), so an analyst viewing a later period can
  // tell which period the displayed content was actually authored for. DD tabs (no period)
  // are unchanged.
  if (!period || metadata.value?.hideTimeStamp) return content;
  return (
    <>
      {content}
      <FieldPeriodStamp field={field ?? null} />
    </>
  );
}

export default function FieldBaseComponent({
  contentItem,
  Component,
}: {
  contentItem: FieldContentItemType;
  Component?: ContentItemDecorator;
}): JSX.Element | null {
  const {
    key: templateKey,
    display_name: displayName,
    question_text: questionText,
  } = contentItem;

  const templateOverride = useMemo(
    () => ({ displayName, questionText }),
    [displayName, questionText]
  );

  const { editMode } = useTemplateStoreFromContext()(
    useShallow(state => ({
      editMode: state.editMode,
    }))
  );

  const cachedFieldData = useFieldValue(templateKey);

  const { field, metadata } = cachedFieldData;

  const currentUser = useCurrentUser();

  if (!metadata.displayType) {
    console.warn("Missing metadata for field", field, metadata, templateKey);
    return null;
  }

  if (
    metadata.managerRestricted &&
    !currentUser.flags.isSubscriptionUser &&
    !currentUser.flags.isClientUser
  ) {
    return null;
  }

  if (
    editMode &&
    InputComponentFor(metadata.displayType) &&
    !metadata.readOnly
  ) {
    return (
      <InputFieldBase
        templateKey={templateKey}
        templateOverride={templateOverride}
        metadata={metadata}
        Component={Component}
      />
    );
  }

  if (
    staticIsNotRenderable({
      value: field?.value,
      displayType: metadata.displayType,
      notApplicable: field?.notApplicable,
      documents: field?.documents,
    })
  )
    return null;

  if (Component) {
    return (
      <Component
        displayName={templateOverride.displayName || metadata.displayName || ""}
        contentItemProps={{
          type: "static",
          displayType: metadata.displayType,
          value: field && field.value,
          metadata,
        }}
      >
        <FieldContent
          metadata={metadata}
          templateKey={templateKey}
          field={field}
        />
      </Component>
    );
  }

  return (
    <FieldContent metadata={metadata} templateKey={templateKey} field={field} />
  );
}
